import React, { useEffect } from 'react';
import styles from '../Dashboard.module.css';
import AssignmentsSummaryCard from './AssignmentsSummaryCard';
import QuizzesSummaryCard from './QuizzesSummaryCard';
import SurveysSummaryCard from './SurveysSummaryCard';
import CoursesSummaryCard from './CoursesSummaryCard';
import NotesSummaryCard from './NotesSummaryCard';

// The My-Assignments / My-Quizzes / My-Courses / My-Notes summary cards
// row (student view only). Extracted VERBATIM from Dashboard's render.
// Whether this student had surveys last time the dashboard finished loading.
// Nothing sensitive, and a wrong guess self-corrects the moment data lands.
const HAD_SURVEYS_KEY = 'faithinsight_dashboard_had_surveys';

const readHadSurveys = () => {
  try {
    return localStorage.getItem(HAD_SURVEYS_KEY) === 'true';
  } catch (e) {
    return false;
  }
};

export default function DashboardSummaryCards({ assignmentSummary, enrolledCourses, formatDueDate, getAssignmentStatus, getQuizStatus, getSurveyStatus, handleAssignmentClick, handleCourseClick, handleQuizClick, handleSurveyClick, loaded, navigate, notesSummary, quizSummary, recentAssignments, recentNotes, recentQuizzes, recentSurveys, setShowPreviousCourses, showPreviousCourses, surveySummary }) {
  const actuallyHasSurveys = surveySummary.open > 0 || surveySummary.completed > 0;

  // surveySummary is zeroed until the fetch lands, so deriving the arrangement
  // straight from it starts every load in the no-surveys shape (Notes filling
  // the survey slot in row 2) and then, for anyone who does have surveys,
  // relocates Notes into a brand-new row 3 -- measured as a 244px row appearing
  // mid-load. Assume last visit's answer until we actually know.
  const hasSurveys = loaded ? actuallyHasSurveys : readHadSurveys();

  useEffect(() => {
    if (!loaded) return;
    try {
      localStorage.setItem(HAD_SURVEYS_KEY, String(actuallyHasSurveys));
    } catch (e) {
      // Private mode / blocked storage: we just guess again next load.
    }
  }, [loaded, actuallyHasSurveys]);

  return (
    <>
      {/* Row 1: Hero Assignments + Quizzes */}
      <div className={styles.topRow}>
        <AssignmentsSummaryCard
          loaded={loaded}
          assignmentSummary={assignmentSummary}
          formatDueDate={formatDueDate}
          getAssignmentStatus={getAssignmentStatus}
          handleAssignmentClick={handleAssignmentClick}
          recentAssignments={recentAssignments}
        />
        <QuizzesSummaryCard
          loaded={loaded}
          formatDueDate={formatDueDate}
          getQuizStatus={getQuizStatus}
          handleQuizClick={handleQuizClick}
          quizSummary={quizSummary}
          recentQuizzes={recentQuizzes}
        />
      </div>

      {/* Row 2: Courses + Surveys (Courses first for mobile priority) — no
          surveys assigned yet, so Notes takes the survey card's usual spot
          here instead of leaving it empty; it moves back below once a
          survey shows up. */}
      <div className={styles.secondRow}>
        <CoursesSummaryCard
          loaded={loaded}
          enrolledCourses={enrolledCourses}
          handleCourseClick={handleCourseClick}
          setShowPreviousCourses={setShowPreviousCourses}
          showPreviousCourses={showPreviousCourses}
        />
        {hasSurveys ? (
          <SurveysSummaryCard
            loaded={loaded}
            formatDueDate={formatDueDate}
            getSurveyStatus={getSurveyStatus}
            handleSurveyClick={handleSurveyClick}
            surveySummary={surveySummary}
            recentSurveys={recentSurveys}
          />
        ) : (
          <NotesSummaryCard
            loaded={loaded}
            navigate={navigate}
            notesSummary={notesSummary}
            recentNotes={recentNotes}
          />
        )}
      </div>

      {/* Row 3: Notes — only when it isn't already filling the survey slot above. */}
      {hasSurveys && (
        <div style={{ marginBottom: '2.25rem' }}>
          <NotesSummaryCard
            loaded={loaded}
            navigate={navigate}
            notesSummary={notesSummary}
            recentNotes={recentNotes}
          />
        </div>
      )}
    </>
  );
}
